import { Animal } from '../types/interfaces';
import { CURRENT_STATE_FIELDS } from '../utils/caravanaChain';

/**
 * Verifica si hay auto-referencia: la caravana del animal está en sus propias caravanas_viejas
 * @param caravana - La caravana del animal actual
 * @param caravanasViejas - Las caravanas viejas del animal actual
 * @returns true si hay auto-referencia
 */
export function hasAutoReferencia(
  caravana: string,
  caravanasViejas?: string[]
): boolean {
  if (!caravanasViejas || caravanasViejas.length === 0) {
    return false;
  }
  return caravanasViejas.includes(caravana);
}

/**
 * Verifica si un animal es referenciado por algún otro animal como caravana vieja.
 * @param caravana - La caravana a buscar
 * @param allAnimals - Todos los animales para buscar referencias
 * @returns true si algún animal tiene esta caravana en sus caravanas_viejas
 */
export function isReferencedAsCaravanaVieja(
  caravana: string,
  allAnimals: Animal[]
): boolean {
  // Use string literal 'RECARAVANEADO' for comparison to ensure consistency
  const RECARAVANEADO = 'RECARAVANEADO';
  return allAnimals.some(
    animal =>
      animal.caravanas_viejas &&
      animal.caravanas_viejas.includes(caravana) &&
      animal.estado !== RECARAVANEADO
  );
}

/**
 * Actualiza el estado de un animal a ACTIVO si ya no es referenciado por ningún otro.
 * @param caravana - La caravana del animal a verificar
 * @param allAnimals - Todos los animales para buscar referencias
 * @returns El nuevo estado ('ACTIVO' o el estado actual si aún es referenciado)
 */
export function getEstadoIfNoLongerReferenced(
  caravana: string,
  currentEstado: string | undefined,
  allAnimals: Animal[]
): string {
  // Use string literals for consistency
  const RECARAVANEADO = 'RECARAVANEADO';
  const ACTIVO = 'ACTIVO';

  // Si el animal no está RECARAVANEADO, no cambiar su estado
  if (currentEstado !== RECARAVANEADO) {
    return currentEstado || ACTIVO;
  }

  // Si ya nadie lo tiene como caravana vieja, volver a ACTIVO
  if (!isReferencedAsCaravanaVieja(caravana, allAnimals)) {
    return ACTIVO;
  }

  // Seguir RECARAVANEADO
  return RECARAVANEADO;
}

/**
 * Busca las caravanas que dejaron de ser referenciadas (fueron removidas de caravanas_viejas).
 * @param oldCaravanasViejas - Caravanas viejas anteriores
 * @param newCaravanasViejas - Caravanas viejas nuevas
 * @returns Caravanas que fueron removidas
 */
export function findRemovedCaravanasViejas(
  oldCaravanasViejas: string[] | undefined,
  newCaravanasViejas: string[] | undefined
): string[] {
  const oldSet = new Set(oldCaravanasViejas || []);
  const newSet = new Set(newCaravanasViejas || []);

  return Array.from(oldSet).filter(caravana => !newSet.has(caravana));
}

/**
 * Busca las caravanas que empezaron a ser referenciadas (fueron agregadas a caravanas_viejas).
 * @param oldCaravanasViejas - Caravanas viejas anteriores
 * @param newCaravanasViejas - Caravanas viejas nuevas
 * @returns Caravanas que fueron agregadas
 */
export function findAddedCaravanasViejas(
  oldCaravanasViejas: string[] | undefined,
  newCaravanasViejas: string[] | undefined
): string[] {
  const oldSet = new Set(oldCaravanasViejas || []);
  const newSet = new Set(newCaravanasViejas || []);

  return Array.from(newSet).filter(caravana => !oldSet.has(caravana));
}

/**
 * Construye un mapa { caravana vieja normalizada → caravana nueva } a partir de
 * todos los animales. Para cada caravana vieja, el valor es la caravana del
 * primer animal NO recaravaneado que la referencia (la caravana activa/terminal
 * que la reemplazó). Como `caravanas_viejas` acumula toda la cadena hacia atrás,
 * esto resuelve cadenas A→B→C al terminal C sin recursión.
 *
 * Las claves se normalizan con trim()+toLowerCase() para tolerar diferencias de
 * espaciado/casing; los valores conservan la caravana original para mostrar.
 *
 * Usado por Mi Ganado para anotar el badge de los animales RECARAVANEADO con la
 * caravana que los reemplazó (KAN-162).
 *
 * @param allAnimals - Todos los animales (típicamente de animal.getAll())
 * @returns Map con clave = caravana vieja normalizada, valor = caravana nueva
 */
export function buildNewCaravanaMap(allAnimals: Animal[]): Map<string, string> {
  // String literal para consistencia con el resto del helper.
  const RECARAVANEADO = 'RECARAVANEADO';
  const map = new Map<string, string>();

  for (const animal of allAnimals) {
    // Solo los animales NO recaravaneado son "dueños" válidos de una caravana vieja.
    if (animal.estado === RECARAVANEADO) continue;
    if (!animal.caravanas_viejas || animal.caravanas_viejas.length === 0) continue;

    for (const vieja of animal.caravanas_viejas) {
      const key = vieja.trim().toLowerCase();
      // First-wins: espeja el .find() de AnimalDetail (primer dueño activo).
      if (!map.has(key)) {
        map.set(key, animal.caravana);
      }
    }
  }

  return map;
}

/**
 * Construye el array de caravanas_viejas para un animal nuevo o existente.
 *
 * Para animales nuevos: combina la caravana_vieja seleccionada con las caravanas_viejas del animal viejo.
 * Para animales existentes: combina las caravanas_viejas actuales con la nueva caravana_vieja y las del animal viejo.
 *
 * @param caravanaVieja - La caravana vieja seleccionada (del autocomplete)
 * @param currentCaravanasViejas - Caravanas viejas del animal actual (solo para updates)
 * @param oldAnimalCaravanasViejas - Caravanas viejas del animal viejo seleccionado
 * @returns Array combinado y deduplicado de caravanas_viejas
 */
export function buildCaravanasViejas(
  caravanaVieja: string,
  currentCaravanasViejas: string[] = [],
  oldAnimalCaravanasViejas: string[] = []
): string[] {
  const combined = [
    caravanaVieja,
    ...currentCaravanasViejas,
    ...oldAnimalCaravanasViejas
  ];

  // Remove duplicates
  const uniqueSet = new Set(combined);
  return Array.from(uniqueSet);
}

/**
 * Calcula la fecha de "primer ingreso" más antigua usando SOLO datos del registro
 * local del animal (rápido, offline). KAN-151.
 *
 * Considera `fecha_de_creacion`, `fecha_de_evento`, `fecha_de_evento_peso_inicial` y la
 * `fecha_evento` de todos los registros embebidos (pesajes, prenies_historial,
 * ubicaciones_historial), recorriendo toda la cadena de caravanas_viejas. Esos arrays sí
 * contienen eventos backdateados de esos tipos.
 *
 * NOTA: los cambios de estado/categoría backdateados (sin peso/ubicación/preñez) no
 * quedan en ningún array local; para esos casos ver `getOldestFechaFromHistorico`.
 *
 * @param caravana - La caravana desde donde iniciar la búsqueda
 * @param getAnimalByCaravana - Función para obtener un animal por su caravana
 * @returns La fecha de ingreso más antigua local en formato ISO, o undefined.
 */
export async function getOldestFechaCreacion(
  caravana: string,
  getAnimalByCaravana: (caravana: string) => Promise<Animal | null>
): Promise<string | undefined> {
  const visited = new Set<string>();
  let oldest: string | undefined = undefined;

  const consider = (date?: string) => {
    if (date && (!oldest || date < oldest)) {
      oldest = date;
    }
  };

  async function visit(caravana: string): Promise<void> {
    // Prevent infinite loops
    if (visited.has(caravana)) {
      console.warn('[getOldestFechaCreacion] Circular reference detected:', caravana);
      return;
    }
    visited.add(caravana);

    const animal = await getAnimalByCaravana(caravana);
    if (!animal) {
      return;
    }

    // Candidatos del registro local actual.
    consider(animal.fecha_de_creacion);
    consider(animal.fecha_de_evento);
    consider(animal.fecha_de_evento_peso_inicial);
    // Historial embebido (sí captura eventos backdateados de estos tipos).
    (animal.pesajes || []).forEach(p => consider(p.fecha_evento));
    (animal.prenies_historial || []).forEach(p => consider(p.fecha_evento));
    (animal.ubicaciones_historial || []).forEach(u => consider(u.fecha_evento));

    // Recorrer TODA la cadena de caravanas_viejas (no solo la primera rama).
    if (animal.caravanas_viejas && animal.caravanas_viejas.length > 0) {
      for (const vieja of animal.caravanas_viejas) {
        await visit(vieja);
      }
    }
  }

  await visit(caravana);
  return oldest;
}

/**
 * Calcula la fecha de "primer ingreso" más antigua consultando el histórico en
 * Supabase (lento, requiere conexión). KAN-151.
 *
 * Recorre la cadena de caravanas_viejas y, para cada caravana, pide sus eventos del
 * histórico (Supabase) tomando el MIN de `fecha_de_evento`. Útil para capturar eventos
 * backdateados que no dejaron rastro en el registro local (p.ej. cambios de
 * estado/categoría sin peso). Diseñada para usarse en segundo plano: si devuelve una
 * fecha más vieja que el valor local, la UI se re-renderiza con esa fecha.
 *
 * @param getHistoricoByCaravana - Devuelve los eventos del histórico de una caravana.
 * @returns La fecha de evento más antigua del histórico en ISO, o undefined.
 */
export async function getOldestFechaFromHistorico(
  caravana: string,
  getAnimalByCaravana: (caravana: string) => Promise<Animal | null>,
  getHistoricoByCaravana: (caravana: string) => Promise<{ fecha_de_evento?: string }[]>
): Promise<string | undefined> {
  const visited = new Set<string>();
  let oldest: string | undefined = undefined;

  const consider = (date?: string) => {
    if (date && (!oldest || date < oldest)) {
      oldest = date;
    }
  };

  async function visit(caravana: string): Promise<void> {
    if (visited.has(caravana)) {
      return;
    }
    visited.add(caravana);

    try {
      const eventos = await getHistoricoByCaravana(caravana);
      for (const evento of eventos) {
        consider(evento.fecha_de_evento);
      }
    } catch (e) {
      console.error('[getOldestFechaFromHistorico] Error leyendo histórico:', e);
    }

    // Recorrer la cadena de caravanas_viejas para cubrir recarvaneos.
    const animal = await getAnimalByCaravana(caravana);
    if (animal?.caravanas_viejas && animal.caravanas_viejas.length > 0) {
      for (const vieja of animal.caravanas_viejas) {
        await visit(vieja);
      }
    }
  }

  await visit(caravana);
  return oldest;
}

/**
 * Obtiene el peso más reciente en la cadena de recarvaneo.
 * Recursivamente busca a través de las caravanas_viejas para encontrar el último peso registrado.
 *
 * @param caravana - La caravana desde donde iniciar la búsqueda
 * @param getAnimalByCaravana - Función para obtener un animal por su caravana
 * @returns El peso más reciente, o undefined si no se encuentra
 */
export async function getLatestWeightFromCaravanChain(
  caravana: string,
  getAnimalByCaravana: (caravana: string) => Promise<Animal | null>
): Promise<number | undefined> {
  const visited = new Set<string>();
  let latestWeight: number | undefined = undefined;
  let latestDate: string | undefined = undefined;

  async function search(caravana: string): Promise<void> {
    // Prevent infinite loops
    if (visited.has(caravana)) {
      console.warn('[getLatestWeightFromCaravanChain] Circular reference detected:', caravana);
      return;
    }
    visited.add(caravana);

    const animal = await getAnimalByCaravana(caravana);
    if (!animal) {
      return;
    }

    // Check current animal's peso
    if (animal.peso !== undefined && animal.peso !== null) {
      const pesoDate = animal.fecha_de_evento || animal.fecha_de_creacion;
      if (!latestDate || (pesoDate && pesoDate > latestDate)) {
        latestWeight = animal.peso;
        latestDate = pesoDate;
      }
    }

    // Check pesajes array for more recent weights
    if (animal.pesajes && animal.pesajes.length > 0) {
      for (const pesaje of animal.pesajes) {
        const pesajeDate = pesaje.fecha_evento || pesaje.fecha_creacion;
        if (pesaje.peso !== undefined && pesaje.peso !== null) {
          if (!latestDate || (pesajeDate && pesajeDate > latestDate)) {
            latestWeight = pesaje.peso;
            latestDate = pesajeDate;
          }
        }
      }
    }

    // If has caravanas_viejas, search recursively in the chain
    if (animal.caravanas_viejas && animal.caravanas_viejas.length > 0) {
      for (const vieja of animal.caravanas_viejas) {
        await search(vieja);
      }
    }
  }

  await search(caravana);
  return latestWeight;
}

/**
 * Options del merge de recarvaneo (KAN-175).
 */
export interface MergeWithOldAnimalOptions {
  /**
   * Campos cuyo valor actual en el formulario son defaults (no elección del
   * usuario ni caché de sesión) y pueden ser pisados por el valor del animal
   * viejo. Caso KAN-175: `categoria` inicia en 'TERNERO' para animales nuevos
   * y ese default no debe bloquear la herencia de la categoría real.
   */
  forceInheritFields?: Set<string>;
  /**
   * Campos que el usuario tocó realmente (distingue un default de una
   * elección). Un campo en `forceInheritFields` que además está acá NO se pisa.
   */
  userTouchedFields?: Set<string>;
  /**
   * OUT: campos de estado actual que este merge copió del animal viejo.
   * El llamador los marca para que el submit los persista (el submit solo
   * envía campos marcados). NO incluye arrays históricos: esos se resuelven
   * por cadena en la lectura (performMerge) y persistirlos duplicaría datos.
   */
  inheritedFields?: Set<string>;
}

/**
 * Merge completo entre animal nuevo y animal viejo para recarvaneo.
 * Combina la información de ambos animales:
 * - Prioridad a lo que el usuario modificó explícitamente
 * - Datos históricos del animal viejo SIEMPRE se incluyen (pesajes, prenias, etc.)
 * - Campos básicos del animal viejo se incluyen si el usuario no los modificó
 * - Con `forceInheritFields`, defaults del form (categoria TERNERO) no bloquean
 *   la herencia, salvo que el usuario haya tocado el campo
 *
 * @param currentData - Datos actuales del formulario (animal nuevo)
 * @param oldAnimal - Datos del animal viejo seleccionado
 * @param userModifiedFields - Campos marcados como modificados (usuario o defaults del form)
 * @param options - Ver MergeWithOldAnimalOptions
 * @returns Objeto con el merge completo de datos
 */
export function mergeWithOldAnimalForRecarvaneo(
  currentData: Record<string, any>,
  oldAnimal: Animal,
  userModifiedFields: Set<string>,
  options?: MergeWithOldAnimalOptions
): Record<string, any> {
  const merged: any = {
    ...currentData,
    caravana_vieja: oldAnimal.caravana,
  };

  // 1. DATOS HISTÓRICOS - SIEMPRE incluir del animal viejo
  // Estos son datos críticos que se transfieren en el recarvaneo
  const historicalFields: string[] = [
    'pesajes',                    // Historial de pesos
    'prenies_historial',         // Historial de preñeces
    'ubicaciones_historial',     // Historial de ubicaciones
    'peso_inicial',              // Peso inicial
    'fecha_de_evento_peso_inicial',
    'fecha_modificacion_peso_inicial',
  ];

  for (const field of historicalFields) {
    const oldAnimalRecord = oldAnimal as unknown as Record<string, unknown>;
    if (oldAnimalRecord[field] !== undefined && oldAnimalRecord[field] !== null) {
      merged[field] = oldAnimalRecord[field];
      console.log(`[MERGE] Incluyendo campo histórico: ${field}`);
    }
  }

  // 2. CAMPOS BÁSICOS - Incluir solo si el usuario NO los modificó
  // El usuario tiene prioridad sobre lo que ingresó manualmente
  const basicFields: string[] = [
    'sexo',
    'ubicacion_id',
    'raza',
    'categoria',
    'edad',
    'prenies',
    'tiene_ternero',
    'observaciones',
    'renspa',
    'fecha_nacimiento',
    'es_comprado',
    'info_compra',
    'info_venta',
    'operacion_compra_id',
    'operacion_venta_id',
    // Parentesco: el animal nuevo hereda madre/padre del viejo (el linaje no
    // cambia con la caravana). Solo si el usuario no los cargó él mismo.
    'madre',
    'padre',
  ];

  for (const field of basicFields) {
    const oldAnimalRecord = oldAnimal as unknown as Record<string, unknown>;
    // ¿Default del form pisable? (KAN-175: p.ej. categoria TERNERO inicial).
    // Solo si el usuario NO tocó realmente el campo: userTouchedFields es la
    // señal autoritativa de elección humana; userModifiedFields también se
    // marca por defaults/cachés del form.
    const forcedByDefault =
      !!options?.forceInheritFields?.has(field) &&
      !options?.userTouchedFields?.has(field);

    // Solo incluir si:
    // - El usuario NO lo modificó explícitamente (o es default pisable)
    // - El animal viejo tiene este campo
    // - El campo actual está vacío o no definido (o es default pisable)
    if (
      (!userModifiedFields.has(field) || forcedByDefault) &&
      oldAnimalRecord[field] !== undefined &&
      oldAnimalRecord[field] !== null &&
      oldAnimalRecord[field] !== '' &&
      (!merged[field] || merged[field] === undefined || merged[field] === '' || forcedByDefault)
    ) {
      merged[field] = oldAnimalRecord[field];
      // Registrar la herencia para que el form la marque y el submit la persista.
      options?.inheritedFields?.add(field);
      console.log(`[MERGE] Copiando campo del viejo: ${field} = ${oldAnimalRecord[field]}`);
    }
  }

  // 3. FECHAS DE ÚLTIMO EVENTO - SIEMPRE incluir del animal viejo
  // Mantienen el historial de cuándo fue la última vez que se modificó cada campo
  const lastEventFields: string[] = [
    'fecha_ultimo_evento_sexo',
    'fecha_ultimo_evento_peso',
    'fecha_ultimo_evento_ubicacion_id',
    'fecha_ultimo_evento_raza',
    'fecha_ultimo_evento_observaciones',
    'fecha_ultimo_evento_prenies',
    'fecha_ultimo_evento_edad',
    'fecha_ultimo_evento_estado',
    'fecha_ultimo_evento_tiene_ternero',
    'fecha_ultimo_evento_categoria',
    'fecha_ultimo_evento_caravanas_viejas',
    'fecha_ultimo_evento_es_comprado',
    'fecha_ultimo_evento_renspa',
  ];

  for (const field of lastEventFields) {
    const oldAnimalRecord = oldAnimal as unknown as Record<string, unknown>;
    if (oldAnimalRecord[field] !== undefined && oldAnimalRecord[field] !== null) {
      merged[field] = oldAnimalRecord[field];
    }
  }

  // 4. FECHAS DE ÚLTIMA MODIFICACIÓN - Solo si el usuario NO modificó el campo
  const lastModFields: string[] = [
    'fecha_ultima_modificacion_sexo',
    'fecha_ultima_modificacion_peso',
    'fecha_ultima_modificacion_ubicacion_id',
    'fecha_ultima_modificacion_raza',
    'fecha_ultima_modificacion_observaciones',
    'fecha_ultima_modificacion_prenies',
    'fecha_ultima_modificacion_edad',
    'fecha_ultima_modificacion_estado',
    'fecha_ultima_modificacion_tiene_ternero',
    'fecha_ultima_modificacion_categoria',
    'fecha_ultima_modificacion_caravanas_viejas',
    'fecha_ultima_modificacion_es_comprado',
    'fecha_ultima_modificacion_renspa',
  ];

  for (const field of lastModFields) {
    const oldAnimalRecord = oldAnimal as unknown as Record<string, unknown>;
    if (!userModifiedFields.has(field) && oldAnimalRecord[field] !== undefined) {
      merged[field] = oldAnimalRecord[field];
    }
  }

  console.log('[MERGE] Merge completo finalizado');
  return merged;
}

/**
 * Red de seguridad de herencia en la CREACIÓN de un animal con caravana_vieja
 * (KAN-175). Si el payload que llega al handler de creación no trae campos de
 * estado actual y el animal viejo los tiene, los copia.
 *
 * Semántica idéntica al backfill de cadena (performMerge): el payload NUNCA se
 * pisa — lo que el usuario cargó o lo que vino de caché de sesión gana; solo se
 * llenan huecos. NO hereda estado ni datos de venta (ciclo de vida de la
 * caravana reemplazada). Es la segunda capa: la primera es el merge del form
 * (mergeWithOldAnimalForRecarvaneo), esta cubre otros paths de creación con
 * caravana_vieja y residuos.
 *
 * Nota: los timestamps fecha_ultimo_evento_* NO se heredan acá — el evento del
 * animal nuevo se registra HOY con los valores heredados.
 *
 * @param payload - Datos del animal a crear/actualizar (no se muta)
 * @param oldAnimal - Animal viejo referenciado como caravana_vieja
 * @returns Nuevo objeto payload con los huecos llenos
 */
export function inheritCurrentStateFieldsForRecarvaneo(
  payload: Record<string, any>,
  oldAnimal: Animal
): Record<string, any> {
  const result: Record<string, any> = { ...payload };
  const old = oldAnimal as unknown as Record<string, unknown>;

  const isEmpty = (v: unknown) => v === undefined || v === null || v === '';

  for (const { field } of CURRENT_STATE_FIELDS) {
    if (!isEmpty(result[field])) continue;
    if (isEmpty(old[field])) continue;
    result[field] = old[field];
  }

  return result;
}
